import { Empty, Modal, Table } from "antd";
import Button from "Components/Button";
import Input from "Components/Input";
import KoboConverter from "Components/KoboConverter";
import Loader from "Components/Loader";
import PriceInput from "Components/PriceInput";
import Textarea from "Components/Textarea";
import { useCreateCreditHistoryMutation } from "hooks/CustomerManagement/useCreateCreditHistoryMutation";
import { useGetCustomerCreditHistoryQuery } from "hooks/CustomerManagement/useGetCustomerCreditHistoryQuery";
import React, { ChangeEvent, useState } from "react";
import Moment from "react-moment";
import { useNavigate, useParams } from "react-router-dom";
import { formatNumberWithCommas } from "utils/formatNumberWithCommas";

const initialState = {
  id: "",
  reason: "",
  amount: "",
};

const Header = () => {
  const navigate = useNavigate();
  const id = useParams();

  const goBack = () => {
    navigate(-1);
  };
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewCreditHistory, setViewCreditHistory] = useState(false);

  const [payload, setPayload] = useState({
    id: id?.id,
    reason: initialState?.reason,
    amount: initialState?.amount,
  });

  const { mutateAsync, isPending } = useCreateCreditHistoryMutation();
  const { data: creditHistory, isPending: isLoadingCreditHistory } =
    useGetCustomerCreditHistoryQuery(id?.id);

  const handleSubmit = () => {
    mutateAsync(payload, {
      onSuccess: () => {
        setIsAddModalOpen(false);
        setPayload(initialState);
      },
    });
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPayload({ ...payload, [name]: value });
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/,/g, "");
    setPayload((prev) => ({ ...prev, [name]: numericValue }));
  };

  const columns = [
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text: any, record: any) => (
        <KoboConverter amount={record?.amount} />
      ),
    },
    {
      title: "Amount Repaid",
      dataIndex: "amountPaid",
      key: "amountPaid",
      render: (amountPaid: any) => <KoboConverter amount={amountPaid} />,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        return (
          <div
            className={`font-bold text-black ${
              status === "approved"
                ? "text-green-500"
                : status === "in_review"
                ? "text-blue-500"
                : status === "declined"
                ? "text-red-500"
                : status === "repaid"
                ? "text-orange-500"
                : status === "pending"
                ? "text-gray-500"
                : ""
            }`}
          >
            {status === "pending" && "Pending"}
            {status === "in_review" && "In Review"}
            {status === "declined" && "Declined"}
            {status === "approved" && "Approved"}
            {status === "repaid" && "Repaid"}
          </div>
        );
      },
    },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => (
        <Moment interval={0} format="DD-MM-YYYY">
          {createdAt}
        </Moment>
      ),
    },
  ];

  return (
    <div className="ml-[20px] mr-[14px] mt-[20px] border-[1px] border-[#eeeeee] p-3 rounded-[10px]">
      <div className="flex items-center justify-between">
        <span className="flex gap-3">
          <span
            className="flex gap-3 items-center font-bold cursor-pointer"
            onClick={goBack}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="size-6 cursor-pointer"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18"
              />
            </svg>
            Back
          </span>
          <div className="text-[#a8a8a8]">
            {">"} Customer Management {">"} Personal Details{" "}
          </div>
        </span>
        <span className="flex gap-3">
          <button
            onClick={() => setViewCreditHistory(true)}
            className="text-[#414141] rounded-md bg-[#FFF5F0] p-3 text-[14px]"
          >
            View Credit History
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="text-[#414141] rounded-md bg-[#FFF5F0] p-3 text-[14px]"
          >
            Add Credit
          </button>
          <button className="text-[#414141] rounded-md bg-[#f3f3f3] p-3 text-[14px]">
            Suspend User
          </button>
        </span>

        <Modal
          title="Credit History"
          width={700}
          open={viewCreditHistory}
          onCancel={() => setViewCreditHistory(false)}
          footer={null}
        >
          {isLoadingCreditHistory && <Loader />}
          <div>
            {creditHistory && creditHistory?.data?.docs.length === 0 ? (
              <Empty />
            ) : (
              <Table
                dataSource={creditHistory && creditHistory.data.docs}
                columns={columns}
                scroll={{ y: "calc(100vh - 200px)" }}
                rowKey="id"
                loading={isPending}
              />
            )}
          </div>
        </Modal>

        <Modal
          title="Add Credit Information"
          open={isAddModalOpen}
          onCancel={() => setIsAddModalOpen(false)}
          footer={null}
        >
          <PriceInput
            name="amount"
            label="Amount"
            value={formatNumberWithCommas(payload?.amount)}
            placeholder="Enter Amount"
            onChange={handleNumberInputChange}
          />

          <Textarea
            label="Reason"
            name="reason"
            value={payload?.reason}
            placeholder="Reason"
            onChange={handleInputChange}
          />
          <Button
            disabled={!payload.amount || !payload.id || !payload.reason}
            onClick={handleSubmit}
            isLoading={isPending}
            label={"Submit"}
          />
        </Modal>
      </div>
    </div>
  );
};

export default Header;
